import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Card, CardHeader, CardBody, FormGroup, Label } from 'reactstrap';

import { CharacterLimitedTextarea } from './CharacterLimitedTextarea/CharacterLimitedTextarea';
import { parseDateTime, formatDateTime, generateTimeOptions } from './timeUtils';

export const SystemMessageCard = (props: any): JSX.Element => {
  const { handleChange, systemMaintenance, setFieldValue, systemMessageOn } = props;
  const [radioOn, setRadioOn] = useState(systemMaintenance?.showSystemMaintenanceMessage);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const showUntilTime = systemMaintenance?.systemMaintenanceShowUntilTime;

  useEffect(() => {
    const { date, time } = parseDateTime(showUntilTime);
    setSelectedDate(date);
    setSelectedTime(time);
  }, [showUntilTime]);

  const handleRadioChange = (value: any) => {
    const isOn = value === 'on';
    setRadioOn(isOn);
    setFieldValue('systemMaintenance.showSystemMaintenanceMessage', isOn);
    if (!isOn) {
      setSelectedDate('');
      setSelectedTime('');
      setFieldValue('systemMaintenance.systemMaintenanceShowUntilTime', null);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    const isoString = formatDateTime(newDate, selectedTime);
    setFieldValue('systemMaintenance.systemMaintenanceShowUntilTime', isoString || undefined);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTime = e.target.value;
    setSelectedTime(newTime);
    const isoString = formatDateTime(selectedDate, newTime);
    setFieldValue('systemMaintenance.systemMaintenanceShowUntilTime', isoString || undefined);
  };

  const timeOptions = generateTimeOptions();

  return (
    <div>
      <Card className="mb-3">
        <CardHeader tag="h5">System Maintenance Messaging</CardHeader>
        <CardBody>
          <div
            className="d-flex align-items-center py-2 px-3 mb-3"
            style={
              systemMessageOn
                ? {
                    border: '1px solid #42A262',
                    borderRadius: '4px',
                    backgroundColor: '#ECF6EF',
                    maxWidth: '500px',
                  }
                : {
                    border: '1px solid #D0342C',
                    borderRadius: '4px',
                    backgroundColor: '#FAEBEA',
                    maxWidth: '500px',
                  }
            }
          >
            <FontAwesomeIcon
              icon={systemMessageOn ? faCheckCircle : faInfoCircle}
              className="mr-2"
              style={{ color: systemMessageOn ? '#42A262' : '#D0342C' }}
            />
            Message {systemMessageOn ? 'enabled' : 'disabled'}.
          </div>

          <FormGroup style={{ maxWidth: '500px' }}>
            <div className="d-flex" style={{ flexDirection: 'column' }}>
              <label className="d-flex align-items-center">
                <input
                  type="radio"
                  name="system-maintenance-radio"
                  value="off"
                  checked={!radioOn}
                  onChange={(e) => handleRadioChange(e.target.value)}
                />
                <span className={`ml-2 ${!radioOn ? 'font-weight-bold' : ''}`}>Message Off</span>
              </label>
              <label className="d-flex align-items-center">
                <input
                  type="radio"
                  name="system-maintenance-radio"
                  value="on"
                  checked={radioOn}
                  onChange={(e) => handleRadioChange(e.target.value)}
                />
                <span className={`ml-2 ${radioOn ? 'font-weight-bold' : ''}`}>Message On</span>
              </label>
            </div>

            {radioOn && (
              <FormGroup style={{ marginLeft: '20px', maxWidth: '500px' }}>
                <Label className="d-block"><span style={{ color: '#EF32DC' }}>(Optional)</span> Automatically remove message after</Label>
                <div className="d-flex" style={{ gap: '10px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="date"
                      id="show-until-date"
                      className="form-control"
                      value={selectedDate}
                      onChange={handleDateChange}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <select
                      id="show-until-time"
                      className="form-control"
                      value={selectedTime}
                      onChange={handleTimeChange}
                    >
                      <option value="">Select time</option>
                      {timeOptions.map((option) => (
                        <option key={option.time} value={option.time}>
                          {option.displayTime}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </FormGroup>
            )}

            <Label for="system-message" className="font-weight-bold d-block">
              Message
            </Label>
            <CharacterLimitedTextarea
              rows={4}
              maxLength={150}
              name="systemMaintenance.systemMaintenanceMessage"
              onChange={handleChange}
              value={systemMaintenance?.systemMaintenanceMessage}
            />
          </FormGroup>

          <div
            className="d-flex align-items-center py-2 px-3"
            style={{
              border: '1px solid #0072B7',
              borderRadius: '4px',
              backgroundColor: '#F5F9FC',
              width: 'fit-content',
            }}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="mr-2" style={{ color: '#0072B7' }} />
            This message will apply across all shards, regardless of which shard is being edited.
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
